import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    flattenLocationTree,
    getLocationNodeTypeLabel,
    LocationNodeCapability,
    LocationNodeTree,
    LocationNodeType
} from '../../models/location-node';
import { LocationNodeService } from '../../services/location-node.service';

@Component({
    selector: 'app-location-node-picker',
    templateUrl: './location-node-picker.component.html',
    styleUrls: ['./location-node-picker.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => LocationNodePickerComponent),
        multi: true
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LocationNodePickerComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    @Input() label = 'Location';
    @Input() placeholder = 'Select a location';
    @Input() required = false;
    @Input() allowClear = true;
    @Input() allowedTypes: LocationNodeType[] = [];
    @Input() requiredCapability: LocationNodeCapability = LocationNodeCapability.None;
    @Input() excludeNodeId?: string;
    @Input() withinNodeId?: string;
    @Output() selectedNodeChange = new EventEmitter<LocationNodeTree>();

    nodes: LocationNodeTree[] = [];
    value?: string;
    disabled = false;
    loading = false;

    private readonly destroy$ = new Subject<void>();
    private onChange: (value?: string) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    constructor(
        private readonly locationNodeService: LocationNodeService,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadNodes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['withinNodeId']?.firstChange && changes['withinNodeId']) {
            this.loadNodes();
        }
        if (changes['allowedTypes'] || changes['requiredCapability'] || changes['excludeNodeId']) {
            this.applyFilters(this.rawNodes);
        }
    }

    writeValue(value?: string): void {
        this.value = value;
        this.changeDetectorRef.markForCheck();
    }

    registerOnChange(fn: (value?: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
        this.changeDetectorRef.markForCheck();
    }

    selectionChanged(value?: string): void {
        this.value = value;
        this.onChange(value);
        this.onTouched();
        const selected = this.nodes.find(node => node.id === value);
        if (selected) {
            this.selectedNodeChange.emit(selected);
        }
    }

    typeLabel(type: LocationNodeType): string {
        return getLocationNodeTypeLabel(type);
    }

    get selectedNode(): LocationNodeTree | undefined {
        return this.nodes.find(node => node.id === this.value);
    }

    optionIndent(node: LocationNodeTree): number {
        return Math.max(0, node.depth) * 14;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private rawNodes: LocationNodeTree[] = [];

    private loadNodes(): void {
        this.loading = true;
        this.locationNodeService.getTree(this.withinNodeId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: tree => {
                    this.rawNodes = flattenLocationTree(tree);
                    this.applyFilters(this.rawNodes);
                    this.loading = false;
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.rawNodes = [];
                    this.nodes = [];
                    this.loading = false;
                    this.changeDetectorRef.markForCheck();
                }
            });
    }

    private applyFilters(nodes: LocationNodeTree[]): void {
        const excludedIds = new Set<string>();
        if (this.excludeNodeId) {
            excludedIds.add(this.excludeNodeId);
            let changed = true;
            while (changed) {
                changed = false;
                (nodes ?? []).forEach(node => {
                    if (node.parentId && excludedIds.has(node.parentId) && !excludedIds.has(node.id)) {
                        excludedIds.add(node.id);
                        changed = true;
                    }
                });
            }
        }

        this.nodes = (nodes ?? []).filter(node => {
            const typeAllowed = !this.allowedTypes?.length || this.allowedTypes.includes(node.type);
            const capabilityAllowed = this.requiredCapability === LocationNodeCapability.None
                || (node.capabilities & this.requiredCapability) === this.requiredCapability;
            return typeAllowed && capabilityAllowed && !excludedIds.has(node.id);
        });
        this.changeDetectorRef.markForCheck();
    }
}
